import React, { useState } from "react";
import {
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import {
  JUDO_PRESENCE_FEATURES,
  JUDO_PRESENCE_PRO,
} from "../data/judoPresenceProData";

import { cn } from "../utils/judoPresenceProUtils";

function FeatureNavigation({
  activeFeatureId,
  onChange,
}) {
  return (
    <div className="border-y border-slate-300/70 dark:border-white/10">
      {JUDO_PRESENCE_FEATURES.map((feature, index) => {
        const Icon = feature.icon;
        const active = feature.id === activeFeatureId;

        return (
          <button
            key={feature.id}
            type="button"
            onClick={() => onChange(feature.id)}
            className={cn(
              "group flex w-full items-start gap-4 py-5 text-left",
              index > 0 &&
                "border-t border-slate-300/70 dark:border-white/10",
            )}
          >
            <span
              className={cn(
                "grid h-10 w-10 shrink-0 place-items-center rounded-full transition",
                active
                  ? "bg-[#0868f2] text-white"
                  : "bg-white text-[#0868f2] group-hover:bg-blue-50 dark:bg-slate-900 dark:text-blue-300",
              )}
            >
              <Icon />
            </span>

            <span className="min-w-0 flex-1">
              <span
                className={cn(
                  "block text-[10px] font-semibold uppercase tracking-[0.17em]",
                  active
                    ? "text-[#ff7900]"
                    : "text-slate-400",
                )}
              >
                {feature.eyebrow}
              </span>

              <span
                className={cn(
                  "mt-1 block text-base font-semibold transition",
                  active
                    ? "text-[#0868f2] dark:text-blue-300"
                    : "text-slate-950 group-hover:text-[#0868f2] dark:text-white",
                )}
              >
                {feature.title}
              </span>
            </span>

            <FaArrowRight
              className={cn(
                "mt-3 shrink-0 text-[10px] transition-all",
                active
                  ? "translate-x-1 text-[#ff7900]"
                  : "text-slate-300 group-hover:translate-x-1 group-hover:text-[#ff7900]",
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function JudoPresenceProFeatures() {
  const [activeFeatureId, setActiveFeatureId] =
    useState("attendance");

  const activeFeature =
    JUDO_PRESENCE_FEATURES.find(
      (feature) => feature.id === activeFeatureId,
    ) || JUDO_PRESENCE_FEATURES[0];

  return (
    <section
      id="presence-features"
      className="scroll-mt-24 overflow-hidden bg-[#f3f6f5] py-20 dark:bg-slate-900 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-300/70 pb-10 dark:border-white/10 lg:grid-cols-[1fr_0.85fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Du cours au tableau de bord
              </p>

              <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Une gestion fluide,
                <span className="block text-[#0868f2] dark:text-blue-300">
                  sans alourdir le cours.
                </span>
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-slate-600 dark:text-slate-300 lg:justify-self-end">
              L’application reste simple pour les enseignants tout en
              offrant aux dirigeants une vision complète de l’activité
              du club.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-12 lg:grid-cols-[0.76fr_1.24fr] lg:items-start xl:gap-16">
          <Reveal>
            <FeatureNavigation
              activeFeatureId={activeFeatureId}
              onChange={setActiveFeatureId}
            />
          </Reveal>

          <Reveal>
            <article key={activeFeature.id}>
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white p-2 shadow-[0_30px_90px_-52px_rgba(15,23,42,0.55)] dark:border-white/10 dark:bg-slate-950">
                <div className="relative aspect-[16/10] overflow-hidden rounded-[18px] bg-slate-100 dark:bg-slate-900">
                  <img
                    src={activeFeature.image}
                    alt={activeFeature.imageAlt}
                    className="h-full w-full object-cover"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent" />

                  <div className="absolute bottom-4 left-4">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-slate-950/50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-md">
                      <FaCheck className="text-[#ff7900]" />
                      {activeFeature.eyebrow}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-7 grid gap-6 lg:grid-cols-[1fr_0.85fr]">
                <div>
                  <h3 className="text-3xl font-semibold leading-tight tracking-[-0.035em] text-slate-950 dark:text-white">
                    {activeFeature.title}
                  </h3>

                  <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                    {activeFeature.description}
                  </p>
                </div>

                <div className="border-t border-slate-300/70 pt-5 dark:border-white/10 lg:border-l lg:border-t-0 lg:pl-7 lg:pt-0">
                  <div className="space-y-3">
                    {activeFeature.points.map((point) => (
                      <div
                        key={point}
                        className="flex items-start gap-3 text-sm leading-6 text-slate-600 dark:text-slate-300"
                      >
                        <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-orange-50 text-[#ff7900] dark:bg-orange-500/10">
                          <FaCheck className="text-[8px]" />
                        </span>

                        <span>{point}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href={JUDO_PRESENCE_PRO.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group mt-6 inline-flex items-center gap-3 text-sm font-semibold text-[#0868f2] dark:text-blue-300"
                  >
                    Découvrir l’application

                    <FaExternalLinkAlt className="text-[9px] transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}